import React, { useState } from 'react';
import { Table, Button, Badge, Modal } from 'react-bootstrap';
import { useFormats } from '../../context/formatContext';

const FormatList = ({ formats, onEdit, onDeleteSuccess, onPublishSuccess }) => {
  const { deleteFormat, togglePublish } = useFormats();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formatToDelete, setFormatToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteClick = (format) => {
    setFormatToDelete(format);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!formatToDelete) return;

    try {
      setDeleting(true);
      await deleteFormat(formatToDelete._id);
      setShowDeleteModal(false);
      setFormatToDelete(null);
      onDeleteSuccess(`Format "${formatToDelete.name}" deleted successfully`);
    } catch (error) {
      console.error('Error deleting format:', error);
    } finally {
      setDeleting(false);
    }
  };

  const handleTogglePublish = async (format) => {
    try {
      await togglePublish(format._id, !format.isPublished);
      onPublishSuccess(
        `Format "${format.name}" ${!format.isPublished ? 'published' : 'unpublished'} successfully`
      );
    } catch (error) {
      console.error('Error toggling publish:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  if (formats.length === 0) {
    return (
      <div className="text-center text-muted my-5">
        <h4>No import formats yet</h4>
        <p>Click "Create New Format" to get started</p>
      </div>
    );
  }

  return (
    <>
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Name</th>
            <th>Status</th>
            <th>Column Mappings</th>
            <th>Club Mappings</th>
            <th>Usage Count</th>
            <th>Last Used</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {formats.map(format => (
            <tr key={format._id}>
              <td>
                <strong>{format.name}</strong>
                {format.sampleFileName && (
                  <small className="d-block text-muted">
                    Sample: {format.sampleFileName}
                  </small>
                )}
              </td>
              <td>
                {format.isPublished ? (
                  <Badge bg="success">Published</Badge>
                ) : (
                  <Badge bg="secondary">Draft</Badge>
                )}
              </td>
              <td>{format.columnMappings?.length || 0}</td>
              <td>{format.clubMappings?.length || 0}</td>
              <td>{format.usageCount || 0}</td>
              <td>{formatDate(format.lastUsedAt)}</td>
              <td>{formatDate(format.createdAt)}</td>
              <td>
                <div className="d-flex gap-1">
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() => onEdit(format)}
                    title="Edit format"
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant={format.isPublished ? 'outline-warning' : 'outline-success'}
                    onClick={() => handleTogglePublish(format)}
                    title={format.isPublished ? 'Unpublish' : 'Publish'}
                  >
                    {format.isPublished ? 'Unpublish' : 'Publish'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => handleDeleteClick(format)}
                    title="Delete format"
                  >
                    Delete
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the format <strong>{formatToDelete?.name}</strong>?
          {formatToDelete?.usageCount > 0 && (
            <div className="alert alert-warning mt-3">
              <strong>Warning:</strong> This format has been used {formatToDelete.usageCount} time(s).
              Sessions using this format will retain their reference, but the format definition will be deleted.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteConfirm}
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default FormatList;
